import {useState, type FormEvent, type KeyboardEvent, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {isAxiosError} from 'axios'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {PencilGlyph} from '@/components/ui/icons'
import {TipoDocumentoSelect} from '@/components/tipo-documento-select'
import {useCorregirCorreo, useCorregirDocumento, type Usuario} from '@/hooks/inci/consola/use-usuarios'
import {publicarExito} from '@/lib/avisos'
import {marcarErrorComoComunicado, translateApiError} from '@/lib/errors'
import {DOCUMENTO_MAX, type TipoDocumento} from '@/lib/documento'
import {validateCedula, validateEmail} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

/** Un dato de la ficha que se corrige donde se lee. El formulario vive en `editor`, montado al
 * abrir: nace con el valor actual y cancelar no deja nada escrito para la próxima vez. */
export function CampoEnLinea({
  valor,
  editarLabel,
  testId,
  editor,
}: {
  valor: ReactNode
  /** El nombre accesible del lápiz: el glifo no se nombra a sí mismo (U17). */
  editarLabel: string
  testId: string
  /** El formulario, montado al abrir. Recibe el cierre: lo llama al guardar y al cancelar. */
  editor: (cerrar: () => void) => ReactNode
}) {
  const [editando, setEditando] = useState(false)
  if (editando) return <>{editor(() => setEditando(false))}</>
  return (
    <span className="flex flex-wrap items-center gap-2">
      {valor}
      <button
        type="button"
        aria-label={editarLabel}
        data-testid={`${testId}-editar`}
        className="rounded text-muted-foreground outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
        onClick={() => setEditando(true)}>
        <PencilGlyph />
      </button>
    </span>
  )
}

/** `noValidate` y Enter/Esc son del componente (R4: nunca la validación emergente del navegador). */
function FormularioEnLinea({
  onSubmit,
  onCancelar,
  pendiente,
  error,
  testId,
  children,
}: {
  onSubmit: () => void
  onCancelar: () => void
  pendiente: boolean
  error: string | null
  testId: string
  children: ReactNode
}) {
  const {t} = useTranslation()
  function submit(e: FormEvent) {
    e.preventDefault()
    onSubmit()
  }
  function teclas(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.stopPropagation()
      onCancelar()
    }
  }
  return (
    <form onSubmit={submit} onKeyDown={teclas} noValidate className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        {children}
        <Button size="sm" type="submit" disabled={pendiente} data-testid={`${testId}-guardar`}>
          {pendiente ? t('common.saving') : t('common.save')}
        </Button>
        <Button size="sm" variant="outline" type="button" onClick={onCancelar} data-testid={`${testId}-cancelar`}>
          {t('common.cancel')}
        </Button>
      </div>
      {error && (
        <p className="text-xs text-destructive" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </form>
  )
}

export function CorreoEditor({usuario, onCerrar}: {usuario: Usuario; onCerrar: () => void}) {
  const {t} = useTranslation()
  const corregir = useCorregirCorreo()
  const [valor, setValor] = useState(usuario.email ?? '')
  const [error, setError] = useState<string | null>(null)

  function guardar() {
    const invalido = validateEmail(valor)
    if (invalido) {
      setError(t(invalido))
      return
    }
    setError(null)
    corregir.mutate(
      {userId: usuario.id, email: valor.trim()},
      {
        onSuccess: () => {
          publicarExito(t('usuarios.corregir.doneTitle'), t('usuarios.corregir.doneBody'), {
            testId: 'usuario-correo-corregido',
          })
          onCerrar()
        },
        onError: (err) => {
          // KAI-224: marcar evita la segunda burbuja de `lib/red-errores-mutacion.ts`; el 409 no pasa
          // por `translateApiError`, que ya marca solo, así que se marca a mano.
          marcarErrorComoComunicado(err)
          // El 409 dice «ya pertenece a otra cuenta»: la pregunta es de quién es la dirección, no si está libre.
          const status = isAxiosError(err) ? err.response?.status : undefined
          setError(status === 409 ? t('usuarios.corregir.enUso') : translateApiError(err))
        },
      }
    )
  }

  return (
    <FormularioEnLinea
      onSubmit={guardar}
      onCancelar={onCerrar}
      pendiente={corregir.isPending}
      error={error}
      testId="usuario-correo">
      <Input
        aria-label={t('usuarios.ficha.email')}
        data-testid="usuario-correo-input"
        inputMode="email"
        autoComplete="email"
        autoFocus
        className="w-64"
        maxLength={tope('MAIL')}
        value={valor}
        onChange={(e) => {
          setValor(e.target.value)
          setError(null)
        }}
        aria-invalid={!!error}
      />
    </FormularioEnLinea>
  )
}

/** Tipo y número en un solo editor: por separado quedaría un estado intermedio inválido (un
 * pasaporte con letras declarado cédula) y un 400 que la pantalla no puede explicar. */
export function DocumentoEditor({usuario, onCerrar}: {usuario: Usuario; onCerrar: () => void}) {
  const {t} = useTranslation()
  const corregir = useCorregirDocumento()
  const [tipo, setTipo] = useState<TipoDocumento>(usuario.tipo_documento)
  const [numero, setNumero] = useState(usuario.cedula)
  const [error, setError] = useState<string | null>(null)

  function guardar() {
    const invalido = validateCedula(numero, tipo)
    if (invalido) {
      setError(t(invalido))
      return
    }
    setError(null)
    corregir.mutate(
      {userId: usuario.id, tipo_documento: tipo, cedula: numero.trim()},
      {
        onSuccess: () => {
          publicarExito(t('usuarios.documento.doneTitle'), t('usuarios.documento.doneBody'), {
            testId: 'usuario-documento-corregido',
          })
          onCerrar()
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <FormularioEnLinea
      onSubmit={guardar}
      onCancelar={onCerrar}
      pendiente={corregir.isPending}
      error={error}
      testId="usuario-documento">
      <TipoDocumentoSelect
        value={tipo}
        onChange={(siguiente) => {
          setTipo(siguiente)
          setError(null)
        }}
        testId="usuario-documento-tipo"
      />
      <Input
        aria-label={t('usuarios.ficha.documento')}
        data-testid="usuario-documento-input"
        autoFocus
        className="w-48 font-mono"
        maxLength={DOCUMENTO_MAX[tipo]}
        value={numero}
        onChange={(e) => {
          setNumero(e.target.value)
          setError(null)
        }}
        aria-invalid={!!error}
      />
    </FormularioEnLinea>
  )
}
